import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { Play, Pause } from 'lucide-react';

const BAR_COUNT = 40;

const pulse = keyframes`
  0%   { transform: scale(1);   opacity: 1;   }
  50%  { transform: scale(1.3); opacity: 0.55;}
  100% { transform: scale(1);   opacity: 1;   }
`;

const Tile = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.backgroundSecondary || p.theme.colors.background};
  min-height: 60px;
`;

const PlayBtn = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 21px;
  border: none;
  cursor: pointer;
  background: ${p => p.theme.colors.tint};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.1s, box-shadow 0.15s;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);

  &:hover { transform: scale(1.05); box-shadow: 0 3px 10px rgba(0, 0, 0, 0.22); }
`;

const BarsWrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2px;
  height: 36px;
  min-width: 0;
  cursor: pointer;
`;

const Bar = styled.div`
  flex: 1;
  min-width: 2px;
  border-radius: 1px;
  height: ${p => Math.max(3, (p.$amp || 0) * 36)}px;
  background: ${p => (p.$active ? p.theme.colors.tint : p.theme.colors.muted + '80')};
  transition: background 0.1s;
`;

const TimeText = styled.span`
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
  flex-shrink: 0;
  min-width: 36px;
  text-align: right;
`;

const TranscribingDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 4px;
  background: ${p => p.theme.colors.tint};
  animation: ${pulse} 1s infinite;
  display: inline-block;
  flex-shrink: 0;
`;

const TranscriptBox = styled.div`
  padding: ${p => p.theme.spacing.sm};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.backgroundSecondary || p.theme.colors.background};
`;

const StatusPill = styled.span`
  display: inline-block;
  padding: 2px 8px;
  margin-left: 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  background: ${p => p.$bg};
`;

const fmt = (s) => {
  const t = Math.max(0, Math.floor(s));
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
};

const computeEnvelope = (audioBuffer, bars = BAR_COUNT) => {
  const ch = audioBuffer.getChannelData(0);
  const block = Math.max(1, Math.floor(ch.length / bars));
  const out = new Array(bars).fill(0);
  for (let i = 0; i < bars; i++) {
    let sum = 0;
    const s = i * block;
    const e = Math.min(ch.length, s + block);
    for (let j = s; j < e; j++) sum += Math.abs(ch[j]);
    out[i] = Math.min(1, (sum / Math.max(1, e - s)) * 2.5);
  }
  return out;
};

const WaveformAudioPlayer = ({ src, theme, attachment }) => {
  const [bars, setBars] = useState(() => new Array(BAR_COUNT).fill(0.35));
  const [playing, setPlaying] = useState(false);
  const [playBar, setPlayBar] = useState(-1);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);
  const decoded = useRef(false);

  useEffect(() => {
    if (!src || decoded.current) return;
    decoded.current = true;
    (async () => {
      try {
        const resp = await fetch(src);
        const buf = await resp.arrayBuffer();
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioCtx();
        const audio = await ctx.decodeAudioData(buf);
        setBars(computeEnvelope(audio));
        ctx.close().catch(() => {});
      } catch (_) { /* keep default bars */ }
    })();
  }, [src]);

  const handlePlayPause = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      el.pause();
      setPlaying(false);
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const el = audioRef.current;
    if (!el || !el.duration || !isFinite(el.duration)) return;
    setCurrentTime(el.currentTime);
    setPlayBar(Math.min(BAR_COUNT - 1, Math.floor((el.currentTime / el.duration) * BAR_COUNT)));
  }, []);

  const handleLoaded = useCallback(() => {
    const el = audioRef.current;
    if (el && isFinite(el.duration)) setDuration(el.duration);
  }, []);

  const handleEnded = useCallback(() => {
    setPlaying(false);
    setPlayBar(-1);
    setCurrentTime(0);
  }, []);

  const handleBarClick = useCallback((e) => {
    const el = audioRef.current;
    if (!el || !el.duration || !isFinite(el.duration)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    el.currentTime = pct * el.duration;
  }, []);

  const st = attachment?.transcriptStatus;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
      <Tile theme={theme}>
        <PlayBtn theme={theme} type="button" onClick={handlePlayPause}>
          {playing ? <Pause size={18} /> : <Play size={18} />}
        </PlayBtn>
        <BarsWrap onClick={handleBarClick}>
          {bars.map((amp, i) => (
            <Bar key={i} theme={theme} $amp={amp} $active={i <= playBar} />
          ))}
        </BarsWrap>
        <TimeText theme={theme}>
          {playing || currentTime > 0 ? fmt(currentTime) : fmt(duration)}
        </TimeText>
      </Tile>

      {st === 'Completed' && attachment?.transcript && (
        <TranscriptBox theme={theme}>
          <div style={{ color: theme.colors.muted, fontSize: '12px', fontWeight: 600, marginBottom: 2 }}>
            Transcript
            <StatusPill $bg={theme.colors.success || '#2e7d32'}>Completed</StatusPill>
            {attachment.transcriptLanguage && (
              <span style={{ marginLeft: 6, color: theme.colors.muted, fontWeight: 400 }}>
                ({attachment.transcriptLanguage})
              </span>
            )}
          </div>
          <div style={{ color: theme.colors.text, fontSize: '13px', whiteSpace: 'pre-wrap' }}>
            {attachment.transcript}
          </div>
        </TranscriptBox>
      )}

      {!attachment?.transcript && st && st !== 'NotRequested' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: theme.colors.muted, fontSize: '12px' }}>
          {(st === 'Pending' || st === 'InProgress') && (
            <>
              <TranscribingDot theme={theme} />
              <span>Transcribing…</span>
            </>
          )}
          {st === 'Failed' && (
            <span style={{ color: theme.colors.danger || '#c62828' }}>
              Transcription failed
              <StatusPill $bg={theme.colors.danger || '#c62828'}>Failed</StatusPill>
            </span>
          )}
        </div>
      )}

      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoaded}
        onEnded={handleEnded}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default WaveformAudioPlayer;
export { WaveformAudioPlayer };
